import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import Select from "react-select"; // Searchable dropdown-kaga machan

const SubClientCreate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [clients, setClients] = useState([]); // Parent Clients list
    const [formData, setFormData] = useState({
        client_id: null, // React-select kaga object-ah maintain panrom
        name: "",
        email: "",
        phone: "",
        gstin: "",
        address: "",
        state_code: ""
    });

    const [errors, setErrors] = useState({});

    // 1. Fetch Parent Clients for Dropdown
    useEffect(() => {
        const fetchParentClients = async () => {
            try {
                const res = await Handler({ method: "GET", url: "/clients/list.php" });
                if (res?.success) {
                    const options = res?.data?.map(c => ({
                        value: c?.id,
                        label: (c?.client?.name || c?.name)?.toUpperCase()
                    })) || [];
                    setClients(options);
                }
            } catch (err) {
                console.error("Error fetching parent clients", err);
            }
        };
        fetchParentClients();
    }, []);

    // 2. Handle Inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const handleSelectChange = (selectedOption) => {
        setFormData({ ...formData, client_id: selectedOption });
        if (errors.client_id) setErrors({ ...errors, client_id: "" });
    };

    // 3. Submit Logic
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation Check
        if (!formData.client_id) return toast.error("Please select a parent client");
        if (!/^[0-9]{10}$/.test(formData.phone)) return toast.error("Valid 10-digit phone number is required");
        if (formData.name.length < 2) return toast.error("Name is too short");

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/sub_client/create.php",
                data: {
                    ...formData,
                    client_id: formData.client_id?.value // ID-ya mattum anupuroom
                }
            });

            if (res?.success) {
                Swal.fire("Success!", "Sub-Client created successfully", "success");
                navigate("/sub-clients"); 
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Failed to create sub-client");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Server connection failed", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Management &gt; Sub-Clients</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Register Sub-Client</h3>
                        <p className="text-muted">Link a new sub-client to an existing parent merchant.</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Form onSubmit={handleSubmit}>
                <Row>
                    {/* Left side: Client & Personal Info */}
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4">Sub-Client Details</h5>
                                <Row>
                                    <Col md={12} className="mb-3">
                                        <FormGroup>
                                            <Label className="fw-bold">Select Parent Client <span className="text-danger">*</span></Label>
                                            <Select
                                                options={clients}
                                                value={formData.client_id}
                                                onChange={handleSelectChange}
                                                placeholder="Search Parent Client..."
                                                isSearchable
                                                className={errors.client_id ? "is-invalid" : ""}
                                            />
                                            {errors.client_id && <div className="text-danger small mt-1">{errors.client_id}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={12} className="mb-3">
                                        <FormGroup>
                                            <Label className="fw-bold">Sub-Client / Branch Name <span className="text-danger">*</span></Label>
                                            <Input
                                                name="name"
                                                placeholder="Enter sub-client name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={errors.name ? "is-invalid" : ""}
                                                required
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Phone Number <span className="text-danger">*</span></Label>
                                            <InputGroup>
                                                <InputGroupText><i className="bx bx-phone"></i></InputGroupText>
                                                <Input
                                                    type="number"
                                                    name="phone"
                                                    placeholder="10 digit mobile"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className={errors.phone ? "is-invalid" : ""}
                                                    required
                                                />
                                            </InputGroup>
                                            {errors.phone && <div className="text-danger small mt-1">{errors.phone}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Email Address</Label>
                                            <InputGroup>
                                                <InputGroupText><i className="bx bx-envelope"></i></InputGroupText>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    placeholder="subclient@example.com"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    className={errors.email ? "is-invalid" : ""}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <FormGroup className="mt-3">
                                    <Label className="fw-bold">Full Address</Label>
                                    <Input
                                        type="textarea"
                                        name="address"
                                        rows="3"
                                        placeholder="Enter sub-client billing address"
                                        value={formData.address}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Right side: GST & Action */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4">Statutory Info</h5>
                                <FormGroup>
                                    <Label className="fw-bold">GSTIN</Label>
                                    <Input
                                        name="gstin"
                                        placeholder="33XXXXX0000X1Z1"
                                        style={{ textTransform: 'uppercase' }}
                                        value={formData.gstin}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                                <FormGroup className="mt-3">
                                    <Label className="fw-bold">State Code</Label>
                                    <Input
                                        type="number"
                                        name="state_code"
                                        placeholder="e.g. 33"
                                        value={formData.state_code}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                                <div className="bg-light p-3 mt-4" style={{ borderRadius: '10px', borderLeft: '4px solid #f16436' }}>
                                    <small className="text-muted">
                                        <i className="bx bx-link-alt me-1"></i>
                                        This sub-client will be billed under the selected Parent Merchant's ledger.
                                    </small>
                                </div>
                            </CardBody>
                        </Card>

                        <Button type="submit" className="add-btn w-100 py-3 fw-bold shadow-sm" style={{ backgroundColor: '#f16436', border: 'none' }}>
                            <i className="bx bx-plus-circle me-1"></i> CREATE SUB-CLIENT
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default SubClientCreate;